import { Link, type Href } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Fonts, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';

/**
 * The "more like this" section rendered at the bottom of an event/tour detail
 * page: a level-2 heading, a "See all →" link, and a flex-wrap grid of cards
 * (passed as children). Purely presentational — the caller owns the data (see
 * the useEvents/useTours seed reuse in the detail pages) so this component has
 * no knowledge of events vs tours.
 *
 * The markup mirrors the city landing page's sections (src/app/[lang]/[city].tsx)
 * so the two read as one design. The <h1> stays the page's own title — this
 * heading is level 2 (`role="heading" aria-level={2}` renders a real <h2> on web
 * via react-native-web while staying valid on native).
 */
export function RelatedList({
  heading,
  seeAllHref,
  children,
  footer,
}: {
  heading: string;
  /** Omitted when `footer` carries the onward links instead. */
  seeAllHref?: Href;
  children: ReactNode;
  /** Rendered under the card grid, in the same column. */
  footer?: ReactNode;
}) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <View style={styles.centered}>
      <View style={styles.page}>
        <View style={styles.sectionHeadingRow}>
          <Text
            role="heading"
            aria-level={2}
            style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {heading}
          </Text>
          {seeAllHref ? (
            <Link href={seeAllHref} asChild>
              <Pressable accessibilityRole="link" style={styles.seeAll}>
                {({ pressed }) => (
                  <>
                    <Text
                      style={[styles.seeAllText, { color: theme.primary }, pressed && { opacity: 0.6 }]}>
                      {t('common.seeAll')}
                    </Text>
                    <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
                  </>
                )}
              </Pressable>
            </Link>
          ) : null}
        </View>
        <View style={styles.grid}>{children}</View>
        {footer}
      </View>
    </View>
  );
}

// Cards are a fixed width (see EventCard/TourCard). Size the block to exactly
// four across so the heading, the "See all →" link and the card row line up as
// one centered column rather than a full-width heading over a detached row.
const CARD_WIDTH = 240;
const RELATED_COLUMNS = 4;
const GRID_MAX_WIDTH = CARD_WIDTH * RELATED_COLUMNS + Spacing.three * (RELATED_COLUMNS - 1);

const styles = StyleSheet.create({
  centered: {
    alignItems: 'center',
    width: '100%',
    marginTop: Spacing.five,
    // Padding lives here (not on `page`) so it doesn't eat into the width cap,
    // and the column still keeps a gutter from the screen edges on phones.
    paddingHorizontal: Spacing.three,
  },
  page: {
    width: '100%',
    maxWidth: GRID_MAX_WIDTH,
    gap: Spacing.three,
  },
  sectionHeadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionHeading: {
    fontSize: 22,
    fontWeight: '700',
  },
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 13,
    fontWeight: '600',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    // Center a partial last row (a city with < 4 upcoming) instead of hugging
    // the left; below the cap the fixed cards wrap and stay centered on phones.
    justifyContent: 'center',
  },
});
